import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, ArrowLeft, Search, X, Check, Plus, RefreshCw, Key, ChevronRight, CreditCard, Banknote, BarChart3, QrCode } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "../../context/AuthContext";
import { PersonActivityCard } from "../finance_admin/PersonActivityCard";

const COLORS = ["#1e3a5f", "#f5a623", "#2d8a4e", "#e74c3c", "#9b59b6"];

export default function ManageGuests() {
  const [guests, setGuests] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [spendingData, setSpendingData] = useState(null);
  const [spendingLoading, setSpendingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [topUpMode, setTopUpMode] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpPaymentType, setTopUpPaymentType] = useState("cash");
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [refundMode, setRefundMode] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => { fetchGuests(); }, []);

  const fetchGuests = async () => {
    setLoading(true);
    try { const res = await authAxios.get("/wallet/guests"); setGuests(res.data); }
    catch (e) { toast.error("Failed to load guests"); }
    finally { setLoading(false); }
  };

  const fetchSpendingData = async (guestId) => {
    setSpendingLoading(true);
    try { const res = await authAxios.get(`/admin/guests/${guestId}/spending`); setSpendingData(res.data); }
    catch (e) { setSpendingData(null); }
    finally { setSpendingLoading(false); }
  };

  const handleSelectGuest = (guest) => {
    setSelectedGuest(guest);
    setTopUpMode(false); setTopUpAmount("");
    fetchSpendingData(guest.guest_id);
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setTopUpLoading(true);
    try {
      await authAxios.post("/wallet/topup", { customer_id: selectedGuest.guest_id, amount, payment_type: topUpPaymentType });
      toast.success(`\u20ac${amount.toFixed(2)} added to wallet`);
      setTopUpAmount(""); setTopUpMode(false);
      fetchGuests(); fetchSpendingData(selectedGuest.guest_id);
      const res = await authAxios.get("/wallet/guests");
      const updated = res.data.find(c => c.guest_id === selectedGuest.guest_id);
      if (updated) setSelectedGuest(updated);
    } catch (e) { toast.error(e.response?.data?.detail || "Top-up failed"); }
    finally { setTopUpLoading(false); }
  };

  const handleRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setRefundLoading(true);
    try {
      await authAxios.post("/wallet/refund", { customer_id: selectedGuest.guest_id, amount });
      toast.success(`\u20ac${amount.toFixed(2)} refunded from wallet`);
      setRefundAmount(""); setRefundMode(false);
      fetchGuests(); fetchSpendingData(selectedGuest.guest_id);
      const res = await authAxios.get("/wallet/guests");
      const updated = res.data.find(c => c.guest_id === selectedGuest.guest_id);
      if (updated) setSelectedGuest(updated);
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setRefundLoading(false); }
  };

  const [filterStatus, setFilterStatus] = useState("active");
  const [filterDuration, setFilterDuration] = useState("");

  const filteredGuests = guests.filter(c => {
    const q = search.toLowerCase();
    const matchesSearch = !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.guest_id?.toLowerCase().includes(q);
    let matchesStatus = true;
    if (filterStatus === "active") matchesStatus = c.is_active !== false;
    else if (filterStatus === "inactive") matchesStatus = c.is_active === false;
    let matchesDuration = true;
    if (filterDuration && c.created_at) {
      const years = Math.floor((new Date() - new Date(c.created_at)) / (365.25 * 24 * 60 * 60 * 1000));
      matchesDuration = years >= parseInt(filterDuration);
    } else if (filterDuration) { matchesDuration = false; }
    return matchesSearch && matchesStatus && matchesDuration;
  });

  const activeCount = guests.filter(c => c.is_active !== false).length;
  const inactiveCount = guests.filter(c => c.is_active === false).length;

  const handleDeactivate = async (guestId, isActive) => {
    try {
      if (isActive) { await authAxios.put(`/wallet/guests/${guestId}/deactivate`); toast.success("Guest deactivated"); }
      else { await authAxios.put(`/wallet/guests/${guestId}/activate`); toast.success("Guest activated"); }
      fetchGuests(); setSelectedGuest(null); setSpendingData(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const handleResetPassword = async (guestId) => {
    try { await authAxios.post(`/wallet/guests/${guestId}/reset-password`); toast.success("Password reset email sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to reset password"); }
  };

  const handleResendCard = async (guestId) => {
    try { await authAxios.post(`/wallet/guests/${guestId}/resend-card`); toast.success("Wallet card email sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send card"); }
  };

  if (selectedGuest) {
    return (
      <div data-testid="guest-detail">
        <button onClick={() => { setSelectedGuest(null); setSpendingData(null); setTopUpMode(false); }} className="flex items-center gap-2 text-slate-600 mb-6"><ArrowLeft className="w-5 h-5" /> Back to Guests</button>
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-[#1e3a5f] flex items-center justify-center"><Users className="w-7 h-7 text-white" /></div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-[#1e3a5f]">{selectedGuest.name}</h2>
              <p className="text-slate-500 text-sm">{selectedGuest.email}</p>
              <p className="text-slate-400 font-mono text-xs">{selectedGuest.guest_id}</p>
            </div>
            <div className="flex items-center gap-2">
              {selectedGuest.is_active ? (
                <>
                  <button onClick={() => handleResetPassword(selectedGuest.guest_id)} className="p-2 rounded-full hover:bg-amber-50 text-amber-500" title="Reset Password" data-testid="reset-password-btn"><Key className="w-5 h-5" /></button>
                  <button onClick={() => handleDeactivate(selectedGuest.guest_id, true)} className="p-2 rounded-full hover:bg-red-50 text-red-500" title="Deactivate" data-testid="toggle-active-btn"><X className="w-5 h-5" /></button>
                </>
              ) : null}
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${selectedGuest.is_active ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{selectedGuest.is_active ? "ACTIVE" : "INACTIVE"}</span>
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-3"><p className="text-slate-500 text-sm">Total Balance</p><p className="text-2xl font-bold text-[#1e3a5f]">{"\u20ac"}{(selectedGuest.balance || 0).toFixed(2)}</p></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-green-50 rounded-lg p-3"><p className="text-green-600 font-bold">{"\u20ac"}{(selectedGuest.perpetual_balance || 0).toFixed(2)}</p><p className="text-slate-500 text-xs">No Expiry</p></div>
              <div className="bg-amber-50 rounded-lg p-3"><p className="text-amber-600 font-bold">{"\u20ac"}{((selectedGuest.balance || 0) - (selectedGuest.perpetual_balance || 0)).toFixed(2)}</p><p className="text-slate-500 text-xs">Non-Refundable</p></div>
            </div>
          </div>
          {selectedGuest.is_active ? (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <button onClick={() => { setTopUpMode(true); setRefundMode(false); }} className={`py-3 rounded-xl font-bold text-sm flex flex-col items-center gap-1 ${topUpMode ? "bg-green-600 text-white" : "bg-green-50 text-green-600 border border-green-200"}`} data-testid="start-topup-btn"><Plus className="w-5 h-5" /> Top Up</button>
            <button onClick={() => { setRefundMode(true); setRefundAmount((selectedGuest.perpetual_balance || 0).toFixed(2)); setTopUpMode(false); }} className={`py-3 rounded-xl font-bold text-sm flex flex-col items-center gap-1 ${refundMode ? "bg-red-500 text-white" : "bg-red-50 text-red-500 border border-red-200"}`} data-testid="start-refund-btn"><RefreshCw className="w-5 h-5" /> Refund</button>
            <button onClick={() => handleResendCard(selectedGuest.guest_id)} className="py-3 rounded-xl font-bold text-sm flex flex-col items-center gap-1 bg-slate-50 text-[#1e3a5f] border border-slate-200" data-testid="resend-card-btn"><QrCode className="w-5 h-5" /> Resend</button>
          </div>
          ) : (
          <div className="bg-red-50 rounded-xl p-3 mb-4 text-center text-sm text-red-600 font-medium">Account is inactive — Top-up and Refund not available</div>
          )}
          {topUpMode && (
            <div className="bg-green-50 rounded-xl p-4 mb-3" data-testid="topup-form">
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[10, 20, 50, 100].map(amt => (
                  <button key={amt} onClick={() => setTopUpAmount(amt.toString())} className={`py-2 rounded-lg text-sm font-bold border-2 transition-colors ${topUpAmount === amt.toString() ? "bg-green-600 text-white border-green-600" : "bg-white text-green-600 border-green-200 hover:border-green-400"}`} data-testid={`quick-topup-${amt}`}>{"\u20ac"}{amt}</button>
                ))}
              </div>
              <input type="number" value={topUpAmount} onChange={e => setTopUpAmount(e.target.value)} placeholder="Custom amount" className="w-full mb-3 p-3 border rounded-xl text-lg font-bold text-center" data-testid="topup-amount-input" />
              <div className="flex gap-2 mb-3">
                <button onClick={() => setTopUpPaymentType("cash")} className={`flex-1 py-2 rounded-lg font-bold text-sm ${topUpPaymentType === "cash" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid="payment-cash"><Banknote className="w-4 h-4 inline mr-1" />Cash</button>
                <button onClick={() => setTopUpPaymentType("card")} className={`flex-1 py-2 rounded-lg font-bold text-sm ${topUpPaymentType === "card" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid="payment-card"><CreditCard className="w-4 h-4 inline mr-1" />Card</button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setTopUpMode(false); setTopUpAmount(""); }} className="flex-1 py-3 rounded-xl border font-bold text-slate-600">Cancel</button>
                <button onClick={handleTopUp} disabled={topUpLoading || !topUpAmount} className="flex-1 py-3 rounded-xl bg-green-600 text-white font-bold disabled:opacity-50" data-testid="confirm-topup-btn">{topUpLoading ? "Processing..." : `Top Up \u20ac${topUpAmount || "0"}`}</button>
              </div>
            </div>
          )}
          {refundMode && (
            <div className="bg-red-50 rounded-xl p-4 mb-3" data-testid="refund-form">
              <p className="text-sm text-red-600 mb-3">Refundable (excl. booking): <span className="font-bold">{"\u20ac"}{(selectedGuest.perpetual_balance || 0).toFixed(2)}</span></p>
              <input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} placeholder="Refund amount" className="w-full mb-3 p-3 border border-red-200 rounded-xl text-lg font-bold text-center" max={selectedGuest.perpetual_balance || 0} data-testid="refund-amount-input" />
              <div className="flex gap-2">
                <button onClick={() => { setRefundMode(false); setRefundAmount(""); }} className="flex-1 py-3 rounded-xl border font-bold text-slate-600">Cancel</button>
                <button onClick={handleRefund} disabled={refundLoading || !refundAmount || parseFloat(refundAmount) <= 0} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold disabled:opacity-50" data-testid="confirm-refund-btn">{refundLoading ? "Processing..." : `Refund \u20ac${refundAmount || "0"}`}</button>
              </div>
            </div>
          )}
        </div>
        <PersonActivityCard kind="guest" id={selectedGuest.guest_id} refreshKey={spendingData} />
      </div>
    );
  }

  return (
    <div data-testid="manage-guests">
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <div className="flex gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email or wallet ID..." className="flex-1 p-3 rounded-xl border" data-testid="guest-search-input" />
          <button className="px-4 py-3 rounded-xl bg-slate-100 text-slate-600"><Search className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
        {[
          { id: "active", label: `Active (${activeCount})` },
          { id: "inactive", label: `Inactive (${inactiveCount})` },
          { id: "all", label: `All (${guests.length})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFilterStatus(f.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${filterStatus === f.id ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid={`filter-${f.id}`}>
            {f.label}
          </button>
        ))}
        <select value={filterDuration} onChange={e => setFilterDuration(e.target.value)} className="px-3 py-1.5 rounded-full border text-xs bg-white" data-testid="filter-duration">
          <option value="">Any Duration</option>
          {[1,2,3,4,5].map(y => <option key={y} value={y}>{y}+ years</option>)}
        </select>
      </div>
      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading...</div>
      ) : filteredGuests.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm"><Users className="w-12 h-12 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">{search ? "No guests match your search" : "No guests registered yet"}</p></div>
      ) : (
        <div className="space-y-2">
          {filteredGuests.map(c => (
            <button key={c.guest_id} onClick={() => handleSelectGuest(c)} className="w-full bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow text-left" data-testid={`guest-${c.guest_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#1e3a5f] flex items-center justify-center shrink-0"><span className="text-white font-bold">{c.name?.charAt(0).toUpperCase()}</span></div>
                  <div className="min-w-0"><p className="font-bold text-[#1e3a5f] truncate">{c.name}</p><p className="text-slate-500 text-sm truncate">{c.email}</p></div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  <div className="text-right"><p className="font-mono text-xs sm:text-sm text-slate-400">{c.guest_id}</p><p className="text-green-600 font-bold">{"\u20ac"}{(c.balance || 0).toFixed(2)}</p></div>
                  <span className={`w-2 h-2 rounded-full ${c.is_active ? "bg-green-500" : "bg-red-500"}`}></span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
